// client/src/components/ClientFidViewer.jsx

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  doc,
  onSnapshot,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  limit,
  orderBy,
  runTransaction,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { firestore, storage } from '../../firebase';
import { FaArrowLeft, FaArrowDown, FaArrowUp, FaGift, FaHourglassHalf, FaShoppingCart, FaStar } from 'react-icons/fa';
import { ref, getDownloadURL, listAll, getMetadata } from 'firebase/storage';
import { sanitize } from '../../utils/sanitize';

// --- Logo avec cache localStorage + TTL 7j ---
const LOGO_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const VIEWER_CACHE_PREFIX = 'clientfidviewer_';
const VIEWER_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const RATING_FLAG_TTL_MS = 24 * 60 * 60 * 1000;
const CELEBRATION_WINDOW_MS = 10 * 60 * 1000; // 10 minutes
const MAX_COMMENT_LENGTH = 280;
const EMPTY_COUNTS = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
const ratingFlagKey = (merchantUid) => `pendingRating_${merchantUid}`;
const CONFETTI_PIECES = [
  { left: '6%', color: '#f97316', duration: '1.35s', delay: '0s', drift: '-40px', start: '-12deg', end: '320deg' },
  { left: '14%', color: '#10b981', duration: '1.5s', delay: '0.05s', drift: '22px', start: '6deg', end: '300deg' },
  { left: '22%', color: '#6366f1', duration: '1.3s', delay: '0.1s', drift: '-18px', start: '-4deg', end: '340deg' },
  { left: '32%', color: '#f59e0b', duration: '1.6s', delay: '0.12s', drift: '28px', start: '8deg', end: '280deg' },
  { left: '42%', color: '#ec4899', duration: '1.25s', delay: '0.18s', drift: '-26px', start: '-16deg', end: '360deg' },
  { left: '50%', color: '#06b6d4', duration: '1.45s', delay: '0.22s', drift: '16px', start: '10deg', end: '300deg' },
  { left: '60%', color: '#f97316', duration: '1.4s', delay: '0.26s', drift: '-14px', start: '-6deg', end: '280deg' },
  { left: '70%', color: '#10b981', duration: '1.55s', delay: '0.3s', drift: '30px', start: '4deg', end: '310deg' },
  { left: '78%', color: '#6366f1', duration: '1.2s', delay: '0.34s', drift: '-32px', start: '-10deg', end: '330deg' },
  { left: '86%', color: '#ec4899', duration: '1.4s', delay: '0.38s', drift: '18px', start: '2deg', end: '350deg' },
  { left: '92%', color: '#06b6d4', duration: '1.3s', delay: '0.42s', drift: '-24px', start: '-8deg', end: '300deg' },
  { left: '98%', color: '#f59e0b', duration: '1.5s', delay: '0.46s', drift: '14px', start: '12deg', end: '320deg' }
];

const buildCountsFromDoc = (data) => {
  const next = { ...EMPTY_COUNTS };
  if (!data) return next;
  if (data.counts && typeof data.counts === 'object') {
    for (let i = 1; i <= 5; i += 1) {
      const val = Number(data.counts[i] ?? data.counts[String(i)]) || 0;
      next[i] = val;
    }
    return next;
  }
  if (Array.isArray(data.ratings)) {
    for (let i = 0; i < data.ratings.length; i += 1) {
      const r = Number(data.ratings[i]?.rating);
      if (r >= 1 && r <= 5) next[r] = (next[r] || 0) + 1;
    }
  }
  return next;
};

const MerchantLogo = ({ merchantId }) => {
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    if (!merchantId) return;

    const cacheKey = `merchantLogo_${merchantId}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      try {
        const { url, ts } = JSON.parse(cached);
        if (url) setLogoUrl(url);
        if (ts && Date.now() - ts < LOGO_TTL_MS) {
          // Cache frais -> on ne touche pas au Storage
          return;
        }
      } catch {}
    }

    let mounted = true;
    (async () => {
      try {
        const logoRef = ref(storage, `${merchantId}/photo-profil/`);
        const logoList = await listAll(logoRef);
        if (logoList.items.length === 0) {
          localStorage.removeItem(cacheKey);
          if (mounted) setLogoUrl(null);
          return;
        }
        const withMeta = await Promise.all(
          logoList.items.map(async (item) => {
            const meta = await getMetadata(item);
            return { item, ts: meta.timeCreated ? new Date(meta.timeCreated).getTime() : 0 };
          })
        );
        withMeta.sort((a, b) => b.ts - a.ts);
        const { item } = withMeta[0];
        const freshUrl = await getDownloadURL(item);
        localStorage.setItem(cacheKey, JSON.stringify({ url: freshUrl, ts: Date.now() }));
        if (mounted) setLogoUrl(freshUrl);
      } catch (e) {
        console.error('Logo merchant:', e);
      }
    })();

    return () => { mounted = false; };
  }, [merchantId]);

  return logoUrl ? (
    <img
      src={logoUrl}
      alt="Logo du commerçant"
      className="cfv-logo"
    />
  ) : null;
};

// ------ FORMAT DATE COMPACT (ex: "Lun 3 dec 2025 11:48") ------
const formatCompactDateFR = (dateObj) => {
  if (!(dateObj instanceof Date) || isNaN(dateObj)) return '—';
  const WD = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const MO = ['jan', 'fev', 'mar', 'avr', 'mai', 'jun', 'jul', 'aou', 'sep', 'oct', 'nov', 'dec'];
  const wd = WD[dateObj.getDay()];
  const d = dateObj.getDate();
  const m = MO[dateObj.getMonth()];
  const y = dateObj.getFullYear();
  const hh = String(dateObj.getHours()).padStart(2, '0');
  const mm = String(dateObj.getMinutes()).padStart(2, '0');
  return `${wd} ${d} ${m} ${y} ${hh}:${mm}`;
};

const formatShortDateFR = (dateObj) => {
  if (!(dateObj instanceof Date) || isNaN(dateObj)) return '—';
  const dd = String(dateObj.getDate()).padStart(2, '0');
  const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
  const yyyy = dateObj.getFullYear();
  const hh = String(dateObj.getHours()).padStart(2, '0');
  const mn = String(dateObj.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${mn}`;
};
// ---------------------------------------------------------------

const ConfettiBanner = ({ active }) => {
  if (!active) return null;
  return (
    <div className="confetti-banner" aria-hidden="true">
      <div className="confetti-message">Cadeau débloqué !</div>
      {CONFETTI_PIECES.map((piece, idx) => (
        <span
          // eslint-disable-next-line react/no-array-index-key
          key={idx}
          className="confetti-piece"
          style={{
            left: piece.left,
            background: piece.color,
            animationDuration: piece.duration,
            animationDelay: piece.delay,
            '--drift': piece.drift,
            '--start-rot': piece.start,
            '--end-rot': piece.end
          }}
        />
      ))}
    </div>
  );
};

// --- Icônes SVG colorées pour la colonne Type de l'historique ---
const IconScan = () => <span className="type-ico type-ico--scan" aria-hidden="true"><FaShoppingCart /></span>;
const IconGift = () => <span className="type-ico type-ico--gift" aria-hidden="true"><FaGift /></span>;
const IconTransferIn = () => <span className="type-ico type-ico--in" aria-hidden="true"><FaArrowDown /></span>;
const IconTransferOut = () => <span className="type-ico type-ico--out" aria-hidden="true"><FaArrowUp /></span>;

// Taille fixe d'un nœud + connecteur minimum → utilisée pour calculer le nombre de colonnes
const NODE_SIZE = 38;
const CONN_MIN = 10;

const StepJourney = ({ steps, completedSteps, current, totalTarget, isGiftReady }) => {
  const containerRef = useRef(null);
  const [cols, setCols] = useState(6);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const compute = () => {
      const w = el.offsetWidth;
      const c = Math.max(2, Math.floor((w + CONN_MIN) / (NODE_SIZE + CONN_MIN)));
      setCols(Math.min(c, 6));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const allNodes = [
    ...steps.map(s => ({ type: 'step', step: s })),
    { type: 'gift' },
  ];

  const rows = [];
  for (let i = 0; i < allNodes.length; i += cols) {
    rows.push(allNodes.slice(i, i + cols));
  }
  // Si le cadeau est seul sur la dernière ligne, on déplace le nœud précédent avec lui
  if (rows.length >= 2 && rows[rows.length - 1].length === 1) {
    const prev = rows[rows.length - 2];
    const moved = prev.pop();
    rows[rows.length - 1].unshift(moved);
  }

  const isNodeDone = (node) =>
    node.type === 'step' ? node.step <= completedSteps : current >= totalTarget;

  const isNodeNext = (node) =>
    node.type === 'step' ? node.step === completedSteps + 1 : isGiftReady;

  const isConnFilled = (origIdx) => isNodeDone(allNodes[origIdx]);

  const renderCircle = (node) => {
    const done = isNodeDone(node);
    const next = isNodeNext(node);
    if (node.type === 'gift') {
      return (
        <div style={{
          width: NODE_SIZE, height: NODE_SIZE, borderRadius: '50%',
          display: 'grid', placeItems: 'center', flexShrink: 0,
          background: done ? 'var(--eb-warning)' : 'var(--eb-warning-bg)',
          border: next ? '2px dashed var(--eb-warning)' : done ? '2px solid var(--eb-warning)' : '2px solid #fde3b0',
          color: done ? '#ffffff' : next ? 'var(--eb-warning)' : '#e8b56a',
          boxShadow: done ? '0 6px 16px rgba(181,71,8,0.28)' : next ? '0 0 0 4px rgba(181,71,8,0.10)' : 'none',
          transition: 'all 0.3s ease',
        }}>
          <FaGift size={14} />
        </div>
      );
    }
    return (
      <div style={{
        width: NODE_SIZE, height: NODE_SIZE, borderRadius: '50%',
        display: 'grid', placeItems: 'center', flexShrink: 0,
        background: done ? 'var(--eb-brand)' : next ? 'var(--eb-brand-50)' : 'var(--eb-surface-2)',
        border: done ? '2px solid var(--eb-brand)' : next ? '2px dashed var(--eb-brand-500)' : '2px solid var(--eb-border)',
        color: done ? '#ffffff' : next ? 'var(--eb-brand)' : 'var(--eb-subtle)',
        fontWeight: 700, fontSize: 13,
        boxShadow: done ? '0 4px 12px rgba(39,76,116,0.25)' : next ? 'var(--eb-ring)' : 'none',
        transition: 'all 0.3s ease',
      }}>
        {done ? '✓' : node.step}
      </div>
    );
  };

  const renderLabel = (node) => {
    const done = isNodeDone(node);
    const next = isNodeNext(node);
    const base = { fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'center', display: 'block', width: NODE_SIZE, whiteSpace: 'nowrap' };
    if (node.type === 'gift') {
      return (
        <span style={{ ...base, color: done || next ? 'var(--eb-warning)' : '#e8b56a' }}>
          Cadeau
        </span>
      );
    }
    return (
      <span style={{ ...base, color: done ? 'var(--eb-brand)' : 'var(--eb-subtle)' }}>
        {done ? 'OK' : `N°${node.step}`}
      </span>
    );
  };

  const hConn = (filled, key) => (
    <div key={key} style={{
      flex: 1, height: 3,
      marginLeft: 2, marginRight: 2,
      background: filled ? 'var(--eb-brand)' : 'var(--eb-border)',
      borderRadius: 999, minWidth: CONN_MIN,
      transition: 'background 0.4s ease',
    }} />
  );

  return (
    <div ref={containerRef} style={{ marginBottom: 20 }}>
      {rows.map((row, rowIdx) => {
        const origStart = rowIdx * cols;
        const isLastRow = rowIdx === rows.length - 1;

        return (
          <div key={rowIdx}>
            {/* Rangée des cercles — le connecteur est dans ce même flex, parfaitement centré */}
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {row.map((node, d) => (
                <React.Fragment key={node.type === 'gift' ? 'gift' : node.step}>
                  {renderCircle(node)}
                  {d < row.length - 1 && hConn(isConnFilled(origStart + d), `hc-${rowIdx}-${d}`)}
                </React.Fragment>
              ))}
            </div>

            {/* Rangée des labels — séparée pour ne pas perturber l'alignement du connecteur */}
            <div style={{ display: 'flex', marginTop: 5, marginBottom: 8 }}>
              {row.map((node, d) => (
                <React.Fragment key={`lbl-${node.type === 'gift' ? 'gift' : node.step}`}>
                  {renderLabel(node)}
                  {d < row.length - 1 && <div style={{ flex: 1, minWidth: CONN_MIN }} />}
                </React.Fragment>
              ))}
            </div>

            {/* Espacement entre lignes */}
            {!isLastRow && <div style={{ height: 10 }} />}
          </div>
        );
      })}
    </div>
  );
};

const ClientFidViewer = () => {
  const { merchantId } = useParams();
  const navigate = useNavigate();

  const [fidelityData, setFidelityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [fidelityType, setFidelityType] = useState(null);
  const [pointsPaliers, setPointsPaliers] = useState([]);
  const [merchantPseudo, setMerchantPseudo] = useState('');
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const [feedbackList, setFeedbackList] = useState([]);
  const [merchantGoogleUrl, setMerchantGoogleUrl] = useState('');
  const [merchantFacebookUrl, setMerchantFacebookUrl] = useState('');
  const [showExternalRating, setShowExternalRating] = useState(false);
  const pendingRatingRef = useRef(false);

  const [uidToPseudo, setUidToPseudo] = useState({});
  const pseudoCacheRef = useRef({});
  const currentUserRef = useRef(null);
  const cachedDocPathRef = useRef(null);

  const makeCacheKey = useCallback((uid) => {
    if (!uid || !merchantId) return null;
    return `${VIEWER_CACHE_PREFIX}${merchantId}_${uid}`;
  }, [merchantId]);

  const loadViewerCache = useCallback((uid) => {
    const key = makeCacheKey(uid);
    if (!key) return null;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      if (parsed.ts && Date.now() - parsed.ts > VIEWER_CACHE_TTL_MS) return null;
      return parsed;
    } catch {
      return null;
    }
  }, [makeCacheKey]);

  const saveViewerCache = useCallback((uid, payload) => {
    const key = makeCacheKey(uid);
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify({ ts: Date.now(), ...payload }));
    } catch {}
  }, [makeCacheKey]);

  const clearViewerCache = useCallback((uid) => {
    const key = makeCacheKey(uid);
    if (!key) return;
    localStorage.removeItem(key);
  }, [makeCacheKey]);

  const derivePseudo = useCallback((data = {}) => {
    return (
      data.pseudo ||
      data.entreprise_name ||
      data.companyName ||
      data.name ||
      data.brand ||
      data.alias ||
      data.displayName ||
      ''
    );
  }, []);

  const inferMerchantMeta = useCallback((seed = {}) => {
    const n = (v, def = 0) => {
      const num = Number(v);
      return Number.isFinite(num) ? num : def;
    };

    const type = seed.Type || seed.type || 'Fidbycount';
    const levels = Array.isArray(seed.levels)
      ? seed.levels
      : [
          { points: seed.points1, gift: seed.gift1 },
          { points: seed.points2, gift: seed.gift2 },
          { points: seed.points3, gift: seed.gift3 },
        ].filter((l) => l && n(l.points) > 0);

    return {
      Type: type,
      levels,
      points1: n(seed.points1),
      points2: n(seed.points2),
      points3: n(seed.points3),
      gift1: seed.gift1 || '',
      gift2: seed.gift2 || '',
      gift3: seed.gift3 || '',
      gift: seed.gift || '',
      multiple: n(seed.multiple || seed.multiplePoints || 1, 1),
      code: seed.code || '',
    };
  }, []);

  // Attache 1 seul listener : tente d'abord "new schema", sinon fallback legacy
  const attachClientSmart = useCallback(async (merchantData, userUid, options = {}) => {
    const { docPath = null, initialData = null } = options;

    const applySnapshot = (snap) => {
      let result = null;
      if (snap.exists()) {
        const d = snap.data() || {};
        result = {
          ...merchantData,
          ...d,
          timestamps: d.timestamps || [],
          resetstamp: d.resetstamp || [],
          pointshistory: d.pointshistory || [],
          transferHistory: d.transferHistory || [],
        };
        // Si le doc n'a plus de code (après validation), on purge tout reliquat provenant du cache/meta
        if (!Object.prototype.hasOwnProperty.call(d, 'code')) {
          delete result.code;
        }
        setFidelityData(result);
      } else {
        setFidelityData(null);
      }
      setLoading(false);
      return result;
    };

    if (docPath) {
      try {
        const docRefCached = doc(firestore, docPath);
        if (initialData) applySnapshot({ exists: () => true, data: () => initialData });
        return onSnapshot(
          docRefCached,
          (snap) => {
            const result = applySnapshot(snap);
            if (snap.exists()) {
              cachedDocPathRef.current = { type: 'new', path: docRefCached.path };

              if (result) saveViewerCache(userUid, { docPath: docRefCached.path, docType: 'new', data: result });
            } else {
              clearViewerCache(userUid);
              cachedDocPathRef.current = null;
            }
          },
          (err) => {
            console.error('Écoute doc cache échouée:', err);
            setLoading(false);
          }
        );
      } catch (e) {
        console.warn('Doc cache invalide, fallback:', e);
      }
    }

    const listColRef = collection(firestore, 'fidelity', merchantId, 'list');

    // 1) Tente new schema (1 lecture max)
    try {
      const qs = await getDocs(query(listColRef, where('clientUid', '==', userUid), limit(1)));
      if (!qs.empty) {
        const clientUidRef = qs.docs[0].ref;
        cachedDocPathRef.current = { type: 'new', path: clientUidRef.path };
        return onSnapshot(
          clientUidRef,
          (snap) => {
            const result = applySnapshot(snap);
            if (snap.exists() && result) {
              saveViewerCache(userUid, { docPath: clientUidRef.path, docType: 'new', data: result });
            } else {
              clearViewerCache(userUid);
              cachedDocPathRef.current = null;
            }
          },
          (err) => {
            console.error('Écoute clientUid:', err);
            setLoading(false);
          }
        );
      }
    } catch (e) {
      console.warn('Recherche clientUid échouée:', e);
    }

    // 2) Fallback legacy
    const legacyRef = doc(firestore, 'fidelity', merchantId, 'list', userUid);
    cachedDocPathRef.current = { type: 'legacy', path: legacyRef.path };
    return onSnapshot(
      legacyRef,
      (snap) => {
        const result = applySnapshot(snap);
        if (snap.exists() && result) {
          saveViewerCache(userUid, { docPath: legacyRef.path, docType: 'legacy', data: result });
        } else {
          clearViewerCache(userUid);
          cachedDocPathRef.current = null;
        }
      },
      (err) => {
        console.error('Écoute legacy client:', err);
        setLoading(false);
      }
    );
  }, [clearViewerCache, merchantId, saveViewerCache]);

  useEffect(() => {
    const auth = getAuth();

    let unsubAuth = null;
    let unsubMerchant = null;
    let unsubClient = null;
    let unsubFeedbacks = null;

    unsubAuth = onAuthStateChanged(auth, async (currentUser) => {
      if (unsubClient) { unsubClient(); unsubClient = null; }
      if (unsubMerchant) { unsubMerchant(); unsubMerchant = null; }
      if (unsubFeedbacks) { unsubFeedbacks(); unsubFeedbacks = null; }

      if (!currentUser) {
        setLoading(false);
        setFidelityData(null);
        if (currentUserRef.current) clearViewerCache(currentUserRef.current);
        currentUserRef.current = null;
        return;
      }
      const userUid = currentUser.uid;
      currentUserRef.current = userUid;

      const cached = loadViewerCache(userUid);
      if (cached?.docPath && cached?.data) {
        cachedDocPathRef.current = { type: cached.docType || 'new', path: cached.docPath };
        setFidelityData(cached.data);
        setLoading(false);
      } else if (!cached) {
        setLoading(true);
      }

      // Feedbacks (notes/commentaires) pour ce marchand
      try {
        const fbRef = collection(firestore, 'Satisfaction', merchantId, 'feedbacks');
        const qFb = query(fbRef, orderBy('createdAt', 'desc'), limit(500));
        unsubFeedbacks = onSnapshot(
          qFb,
          (snap) => {
            const arr = snap.docs.map((d) => {
              const data = d.data() || {};
              const txMs = typeof data?.txDateMillis === 'number'
                ? data.txDateMillis
                : (typeof data?.createdAt?.toMillis === 'function' ? data.createdAt.toMillis() : null);
              const rating = Number(data?.rating) || 0;
              const uid = data?.uid || null;
              return { txMs, rating, uid };
            }).filter(fb => fb.txMs && fb.rating && fb.uid);
            setFeedbackList(arr);
          },
          (err) => {
            console.error('Feedbacks snapshot error:', err);
          }
        );
      } catch (e) {
        console.error('Feedbacks snapshot error:', e);
      }

      // Pseudo marchand : priorité à "users" (source de vérité de MerchantFidGestion),
      // fallback sur "entreprises" (par id puis par userId)
      let pseudo = 'Inconnu';
      try {
        const userSnap = await getDoc(doc(firestore, 'users', merchantId));
        if (userSnap.exists()) {
          const p = derivePseudo(userSnap.data() || {});
          if (p) pseudo = p;
        }
      } catch {}
      if (pseudo === 'Inconnu') {
        try {
          const entSnap = await getDoc(doc(firestore, 'entreprises', merchantId));
          if (entSnap.exists()) {
            const p = derivePseudo(entSnap.data() || {});
            if (p) pseudo = p;
          }
        } catch {}
      }
      if (pseudo === 'Inconnu') {
        try {
          const qs = await getDocs(
            query(collection(firestore, 'entreprises'), where('userId', '==', merchantId), limit(1))
          );
          if (!qs.empty) {
            const p = derivePseudo(qs.docs[0].data() || {});
            if (p) pseudo = p;
          }
        } catch {
          // ignore
        }
      }
      setMerchantPseudo(pseudo);
      // Note: pseudo sera écrasé plus bas par fidelity/{merchantId}.pseudo si présent

      const docCache = cached?.data || {};
      const fallbackMeta = inferMerchantMeta(docCache);

      // Tous les utilisateurs peuvent désormais lire le doc merchant fidelity (règles assouplies)
      const merchantDocRef = doc(firestore, 'fidelity', merchantId);
      const merchantSnap = await getDoc(merchantDocRef).catch(() => null);

      const metaFromMerchant = merchantSnap?.exists()
        ? inferMerchantMeta({ ...fallbackMeta, ...(merchantSnap.data() || {}) })
        : fallbackMeta;

      setFidelityType(metaFromMerchant.Type || 'Fidbycount');
      setPointsPaliers(metaFromMerchant.Type === 'Fidbypoints' ? metaFromMerchant.levels || [] : []);

      // Pseudo depuis fidelity/{merchantId} (écrit par MerchantFidGestion, accessible à tous)
      if (merchantSnap?.exists()) {
        const fidPseudo = merchantSnap.data()?.pseudo;
        if (fidPseudo) setMerchantPseudo(fidPseudo);
      }

      const satMode = merchantSnap?.exists() ? (merchantSnap.data()?.satisfactionMode || 'ecobill') : 'ecobill';
      const googleUrl = merchantSnap?.exists() ? (merchantSnap.data()?.googleReviewUrl || '') : '';
      const facebookUrl = merchantSnap?.exists() ? (merchantSnap.data()?.facebookReviewUrl || '') : '';
      setMerchantGoogleUrl(googleUrl);
      setMerchantFacebookUrl(facebookUrl);

      // Si une note était en attente, on ouvre maintenant le bon prompt
      if (pendingRatingRef.current) {
        pendingRatingRef.current = false;
        if (satMode !== 'ecobill') {
          setShowExternalRating(true);
        } else {
          setShowRatingModal(true);
          setRatingValue(0);
          setRatingComment('');
        }
      }

      // Les marchands gardent une écoute temps réel sur leur doc de config
      if (userUid === merchantId) {
        unsubMerchant = onSnapshot(
          merchantDocRef,
          async (snap) => {
            const updatedMeta = snap.exists()
              ? inferMerchantMeta({ ...metaFromMerchant, ...(snap.data() || {}) })
              : metaFromMerchant;
            setFidelityType(updatedMeta.Type || 'Fidbycount');
            setPointsPaliers(updatedMeta.Type === 'Fidbypoints' ? updatedMeta.levels || [] : []);
            const options = cachedDocPathRef.current?.path
              ? { docPath: cachedDocPathRef.current.path, initialData: cached?.data }
              : {};
            if (unsubClient) { unsubClient(); }
            unsubClient = await attachClientSmart(updatedMeta, userUid, options);
          },
          (err) => console.error('Écoute merchant:', err)
        );
      }

      const options = cachedDocPathRef.current?.path
        ? { docPath: cachedDocPathRef.current.path, initialData: cached?.data }
        : {};
      unsubClient = await attachClientSmart(metaFromMerchant, userUid, options);
      setLoading(false);
    });

    return () => {
      if (unsubClient) { unsubClient(); unsubClient = null; }
      if (unsubMerchant) { unsubMerchant(); unsubMerchant = null; }
      if (unsubFeedbacks) { unsubFeedbacks(); unsubFeedbacks = null; }
      if (unsubAuth) { unsubAuth(); unsubAuth = null; }
    };
  }, [attachClientSmart, clearViewerCache, derivePseudo, loadViewerCache, merchantId, inferMerchantMeta]);

  // Invite à laisser une note quand le code vient d'être utilisé
  useEffect(() => {
    if (!merchantId) return;
    const key = ratingFlagKey(merchantId);
    let shouldOpen = false;
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        localStorage.removeItem(key);
        const parsed = JSON.parse(raw);
        const ts = typeof parsed?.ts === 'number' ? parsed.ts : 0;
        if (!ts || Date.now() - ts < RATING_FLAG_TTL_MS) {
          shouldOpen = true;
        }
      }
    } catch {
      // silencieux
    }
    if (shouldOpen) {
      // On stocke le déclenchement ; le bon modal sera ouvert une fois
      // les données du marchand (satisfactionMode) chargées.
      pendingRatingRef.current = true;
    }
  }, [merchantId]);

  const handleBackClick = () => navigate('/clientfid');

  // Résolution lazy des pseudos (mini cache)
  useEffect(() => {
    const loadMissingPseudos = async () => {
      const arr = fidelityData?.transferHistory || [];
      if (!arr.length) return;
      const missing = new Set();
      arr.forEach((t) => {
        if (t?.direction === 'in' && t?.fromUid && !pseudoCacheRef.current[t.fromUid]) missing.add(t.fromUid);
        if (t?.direction === 'out' && t?.toUid && !pseudoCacheRef.current[t.toUid]) missing.add(t.toUid);
      });
      if (!missing.size) return;

      const entries = await Promise.all(
        Array.from(missing).map(async (uid) => {
          try {
            const snap = await getDoc(doc(firestore, 'users', uid));
            const pseudo = snap.exists() ? (snap.data()?.pseudo || 'Utilisateur') : 'Utilisateur';
            return [uid, pseudo];
          } catch {
            return [uid, 'Utilisateur'];
          }
        })
      );

      const next = { ...pseudoCacheRef.current };
      const add = {};
      entries.forEach(([uid, pseudo]) => { next[uid] = pseudo; add[uid] = pseudo; });
      pseudoCacheRef.current = next;
      setUidToPseudo((prev) => ({ ...prev, ...add }));
    };
    loadMissingPseudos();
  }, [fidelityData?.transferHistory]);

  const toDate = (x) => (typeof x?.toDate === 'function' ? x.toDate() : new Date(x || 0));

  const flattenedHistory = useMemo(() => {
    if (!fidelityData) return [];

    const timestamps = (fidelityData.timestamps || []).map((ts, i) => ({
      id: `ts-${i}`, date: toDate(ts.date), type: 'timestamp', count: ts.count ?? 1,
    }));

    const resetstamps = (fidelityData.resetstamp || []).map((rs, i) => ({
      id: `rs-${i}`, date: toDate(rs.date), type: 'resetstamp', count: rs.count ?? 1, giftAtTime: rs.giftAtTime,
    }));

    const pointshistory = (fidelityData.pointshistory || []).map((ph, i) => ({
      id: `ph-${i}`, date: toDate(ph.date), type: 'pointshistory', points: ph.points, montant: ph.montant, gift: ph.gift,
    }));

    const transfers = (fidelityData.transferHistory || []).map((t, i) => ({
      id: `tr-${t.txId || i}`,
      date: new Date(Number(t.at || 0) || 0),
      type: 'transfer',
      direction: t.direction,
      kind: t.kind,
      qty: Number(t.qty || 0),
      fromUid: t.fromUid || null,
      toUid: t.toUid || null,
      txId: t.txId || null,
    }));

    return [...timestamps, ...resetstamps, ...pointshistory, ...transfers].sort((a, b) => b.date - a.date);
  }, [fidelityData]);

  const justUnlockedGift = useMemo(() => {
    if (!showRatingModal || fidelityType !== 'Fidbycount') return false;
    if (!flattenedHistory.length) return false;
    const latest = flattenedHistory[0];
    if (!latest?.date || !(latest.date instanceof Date) || isNaN(latest.date)) return false;
    if (latest.type !== 'resetstamp') return false;
    const delta = Date.now() - latest.date.getTime();
    return delta >= 0 && delta < CELEBRATION_WINDOW_MS;
  }, [flattenedHistory, fidelityType, showRatingModal]);

  const findRatingForRow = useCallback((row) => {
    if (!row || !row.date) return 0;
    const txMs = row.date instanceof Date ? row.date.getTime() : null;
    if (!txMs) return 0;
    const currentUid = currentUserRef.current;
    let best = 0;
    let bestDiff = Infinity;
    feedbackList.forEach((fb) => {
      if (!fb || !fb.txMs || !fb.rating || !fb.uid) return;
      if (currentUid && fb.uid !== currentUid) return;
      const diff = Math.abs(fb.txMs - txMs);
      if (diff <= 120000 && diff < bestDiff) { // tolérance 2 min
        bestDiff = diff;
        best = fb.rating;
      }
    });
    return best;
  }, [feedbackList]);

  const renderFidelityPoints = (count, total, gift) => {
    const totalTarget = Math.max(1, Number(total) || 1);
    const stepsBeforeGift = Math.max(1, totalTarget - 1);
    const current = Math.max(0, Number(count) || 0);
    const completedSteps = Math.min(current, stepsBeforeGift);
    const progressPercent = Math.min(100, Math.round((current / totalTarget) * 100));
    const remaining = Math.max(0, totalTarget - current);
    const steps = Array.from({ length: stepsBeforeGift }, (_, i) => i + 1);
    const isGiftReady = current >= stepsBeforeGift && current < totalTarget;

    return (
      <div className="cfv-loyalty">
        {/* En-tête marque */}
        <div className="cfv-loyalty__head">
          <div className="cfv-loyalty__deco cfv-loyalty__deco--a" />
          <div className="cfv-loyalty__deco cfv-loyalty__deco--b" />

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ textAlign: 'center' }}>
              <div className="cfv-loyalty__eyebrow">Carte de fidélité</div>
              <div className="cfv-loyalty__count">
                {current}
                <span className="cfv-loyalty__total">/ {totalTarget}</span>
              </div>
              <div className="cfv-loyalty__caption">passages effectués</div>
            </div>

            {/* Barre de progression */}
            <div
              className="cfv-progress"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={progressPercent}
            >
              <div className="cfv-progress__fill" style={{ width: `${progressPercent}%` }} />
            </div>
          </div>
        </div>

        {/* Corps */}
        <div className="cfv-loyalty__body">
          {/* Parcours des étapes — snake adaptatif */}
          <StepJourney
            steps={steps}
            completedSteps={completedSteps}
            current={current}
            totalTarget={totalTarget}
            isGiftReady={isGiftReady}
          />

          {/* Message dynamique */}
          {remaining <= 1 && remaining > 0 && (
            <div className="cfv-almost">
              <FaGift aria-hidden="true" /> Plus qu'un passage pour votre cadeau !
            </div>
          )}

          {/* Récompense */}
          <div className="cfv-reward">
            <div className="cfv-reward__icon">
              <FaGift size={17} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="cfv-eyebrow">Récompense</div>
              <div className="cfv-reward__gift">{gift || 'Cadeau à gagner'}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleRatingSubmit = async () => {
    if (!merchantId || ratingValue === 0 || ratingSubmitting) return;
    const userUid = currentUserRef.current;
    if (!userUid) {
      alert('Vous devez être connecté pour noter.');
      return;
    }
    const latestTx = flattenedHistory.length > 0 ? flattenedHistory[0].date : null;
    const txDateMillis = latestTx instanceof Date && !isNaN(latestTx) ? latestTx.getTime() : null;
    setRatingSubmitting(true);
    try {
      const userDocSnap = await getDoc(doc(firestore, 'users', userUid));
      const authorPseudo = userDocSnap.exists() ? (userDocSnap.data()?.pseudo || 'Inconnu') : 'Inconnu';
      const sanitizedComment = sanitize(ratingComment).slice(0, MAX_COMMENT_LENGTH);

      const satRef = doc(firestore, 'Satisfaction', merchantId);
      await runTransaction(firestore, async (transaction) => {
        const snap = await transaction.get(satRef);
        const existing = snap.exists() ? snap.data() : null;
        const counts = buildCountsFromDoc(existing);
        counts[ratingValue] = (counts[ratingValue] || 0) + 1;
        const totalRatings = (Number(existing?.totalRatings) || 0) + 1;
        const totalScore = (Number(existing?.totalScore) || 0) + ratingValue;

        transaction.set(
          satRef,
          {
            pseudo: merchantPseudo || 'Inconnu',
            counts,
            totalRatings,
            totalScore,
            updatedAt: serverTimestamp()
          },
          { merge: true }
        );
      });

      const feedbacksRef = collection(firestore, 'Satisfaction', merchantId, 'feedbacks');
      const payload = {
        uid: userUid,
        rating: ratingValue,
        pseudo: authorPseudo,
        source: 'client',
        createdAt: serverTimestamp(),
        txDateMillis: txDateMillis || Date.now()
      };
      if (sanitizedComment) payload.comment = sanitizedComment;
      await addDoc(feedbacksRef, payload);

      handleRatingClose();
    } catch (error) {
      console.error('Erreur lors de la soumission de la note :', error);
      setRatingSubmitting(false);
    }
  };

  const handleRatingClose = () => {
    setShowRatingModal(false);
    setRatingSubmitting(false);
    setRatingValue(0);
    setRatingComment('');
  };

  return (
    <div className="cfv-page">
      {showExternalRating && (
        <div className="cfv-overlay">
          <div className="cfv-modal cfv-modal--center eb-fade-up" role="dialog" aria-modal="true">
            <MerchantLogo merchantId={merchantId} />
            <h3 className="cfv-modal__title">{merchantPseudo || 'Commerçant'}</h3>
            <p className="cfv-modal__text">Comment s&apos;est passée votre visite ?</p>
            <div className="cfv-social">
              {merchantGoogleUrl && (
                <a
                  href={merchantGoogleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setShowExternalRating(false)}
                  className="cfv-social__btn cfv-social__btn--google"
                >
                  <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" style={{ width: 20, height: 20, flexShrink: 0 }}>
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.08 17.74 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-3.59-13.46-8.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                    <path fill="none" d="M0 0h48v48H0z"/>
                  </svg>
                  Laisser un avis Google
                </a>
              )}
              {merchantFacebookUrl && (
                <a
                  href={merchantFacebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setShowExternalRating(false)}
                  className="cfv-social__btn cfv-social__btn--facebook"
                >
                  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ width: 22, height: 22, flexShrink: 0 }}>
                    <circle cx="12" cy="12" r="11" fill="#ffffff" />
                    <path fill="#1877F2" d="M13.5 8.5H15V6h-2c-1.93 0-3 1.07-3 3v1.5H8.5V13H10v8h2.5v-8H14l.5-2.5H12.5V9c0-.28.22-.5.5-.5h.5z"/>
                  </svg>
                  Laisser un avis Facebook
                </a>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowExternalRating(false)}
              className="eb-btn eb-btn--ghost cfv-later"
            >
              Plus tard
            </button>
          </div>
        </div>
      )}

      {showRatingModal && (
        <div className="cfv-overlay">
          <div className="cfv-modal eb-fade-up" role="dialog" aria-modal="true">
            <ConfettiBanner active={justUnlockedGift} />
            <div className="cfv-modal__center">
              <MerchantLogo merchantId={merchantId} />
              <h3 className="cfv-modal__title">{merchantPseudo || 'Commerçant'}</h3>
              <p className="cfv-modal__text">Comment s'est passée votre visite ?</p>
            </div>
            <div className="cfv-stars" role="radiogroup" aria-label="Note">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  role="radio"
                  aria-checked={ratingValue === star}
                  aria-label={`${star} étoile${star > 1 ? 's' : ''}`}
                  onClick={() => setRatingValue(star)}
                  className={`cfv-star${ratingValue >= star ? ' is-on' : ''}${ratingValue === star ? ' is-current' : ''}`}
                >
                  <FaStar />
                </button>
              ))}
            </div>
            <textarea
              value={ratingComment}
              onChange={(e) => setRatingComment(e.target.value.slice(0, MAX_COMMENT_LENGTH))}
              placeholder="Laissez un commentaire (optionnel)"
              rows={4}
              className="cfv-textarea"
            />
            <div className="cfv-counter">
              {ratingComment.length}/{MAX_COMMENT_LENGTH}
            </div>
            <div className="cfv-modal__actions">
              <button
                type="button"
                onClick={handleRatingSubmit}
                disabled={ratingValue === 0 || ratingSubmitting}
                className="eb-btn eb-btn--primary eb-btn--lg"
              >
                {ratingSubmitting ? <span className="eb-spinner eb-spinner--sm eb-spinner--light" aria-hidden="true" /> : null}
                {ratingSubmitting ? 'Envoi...' : 'Envoyer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- Styles page, modale note + historique --- */}
      <style>{CFV_STYLES}</style>

      <div className="cfv-main">
        <div className="cfv-topbar">
          <button type="button" onClick={handleBackClick} className="eb-btn eb-btn--secondary eb-btn--sm cfv-back">
            <FaArrowLeft aria-hidden="true" /> Retour
          </button>
        </div>

        {loading ? (
          <div className="cfv-state">
            <span className="eb-spinner" aria-hidden="true" />
            <p className="eb-muted" style={{ margin: 0 }}>Chargement...</p>
          </div>
        ) : fidelityData ? (
          <section className="cfv-hero">
            <div className="cfv-merchant">
              <MerchantLogo merchantId={merchantId} />
              {merchantPseudo && <h1 className="cfv-merchant__name">{merchantPseudo}</h1>}
            </div>

            {fidelityData && fidelityData.code && (
              <div className="cfv-code" aria-label={`Code ${fidelityData.code}`}>
                {fidelityData.code}
              </div>
            )}

            {fidelityType === 'Fidbycount' && (
              renderFidelityPoints(fidelityData.compteur, fidelityData.multiple, fidelityData.gift)
            )}

            {fidelityType === 'Fidbypoints' && (
              <div className="cfv-points">
                <div className="cfv-points__head">
                  <div className="cfv-loyalty__eyebrow">Vos points</div>
                  <div className="cfv-loyalty__count">
                    {fidelityData.points || 0}
                    <span className="cfv-loyalty__total">pts</span>
                  </div>
                </div>
                <div
                  className="cfv-points__list"
                  style={{ maxHeight: pointsPaliers.length > 3 ? '260px' : 'none', overflowY: pointsPaliers.length > 3 ? 'auto' : 'visible' }}
                >
                  {pointsPaliers.map((palier, index) => {
                    if (!(palier.points > 0)) return null;
                    const reached = (fidelityData.points || 0) >= palier.points;
                    return (
                      <div key={index} className={`cfv-tier${reached ? ' is-reached' : ''}`}>
                        <div className="cfv-tier__icon" aria-hidden="true">
                          {reached ? <FaGift /> : <FaHourglassHalf />}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <p className="cfv-tier__gift"><strong>{palier.gift}</strong></p>
                          <p className="cfv-tier__pts">{palier.points} points</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        ) : (
          <div className="cfv-state">
            <p className="eb-muted" style={{ margin: 0 }}>Aucune donnée trouvée.</p>
          </div>
        )}

        {/* -------- HISTORIQUE (même structure, format date compact) -------- */}
        <section className="cfv-history">
          <h2 className="cfv-history__title">Historique</h2>
          {(() => {
            const rows = flattenedHistory;
            if (!rows.length) return <div className="cfv-state cfv-state--flat"><p className="eb-muted" style={{ margin: 0 }}>Aucun historique des transactions.</p></div>;

            const allRows = rows;
            const visible = showAll ? allRows : allRows.slice(0, 5);

            return (
              <>
                <div className="table-fidelity-wrap">
                  <table className="table-fidelity">
                    <thead>
                      <tr>
                        <th>{fidelityType === 'Fidbypoints' ? 'Points' : 'Scans'}</th>
                        <th>Date</th>
                        <th style={{ textAlign: 'center' }}>Note</th>
                        <th>Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map((row, index) => {
                        const d = (row?.date instanceof Date && !isNaN(row.date)) ? row.date : new Date(0);
                        const formattedDate = formatCompactDateFR(d);
                        const formattedDateShort = formatShortDateFR(d);
                        const ratingVal = findRatingForRow(row);

                        let typeLabel = '';
                        let typeIcon = null;
                        let value = '';
                        let rowStyle = {};
                        let valueClass = 'pill';
                        let rowClassName = '';

                        switch (row.type) {
                          case 'timestamp':
                            typeLabel = 'Scan';
                            typeIcon = <IconScan />;
                            value = `+${row.count || 1}`;
                            valueClass += ' pill-scan';
                            break;

                          case 'resetstamp':
                            typeLabel = row.giftAtTime || 'Gain';
                            typeIcon = <IconGift />;
                            value = `x${row.count || 1}`;
                            valueClass += ' pill-gift';
                            rowStyle = {};
                            rowClassName = 'row-gift';
                            break;

                          case 'pointshistory':
                            if (row.points > 0) {
                              typeLabel = 'Achat';
                              typeIcon = <IconScan />;
                              value = `+${row.points} pts`;
                              valueClass += ' pill-pos';
                            } else {
                              typeLabel = row.gift || 'Gain';
                              typeIcon = <IconGift />;
                              value = `${row.points} pts`;
                              valueClass += ' pill-gift';
                              rowStyle = {};
                              rowClassName = 'row-gift';
                            }
                            break;

                          case 'transfer': {
                            const unit = row.kind === 'passages' ? 'passages' : 'pts';
                            if (row.direction === 'in') {
                              const fromName = row.fromUid ? (uidToPseudo[row.fromUid] || 'Utilisateur') : 'Utilisateur';
                              typeLabel = `Reçu de ${fromName}`;
                              typeIcon = <IconTransferIn />;
                              value = `+${row.qty} ${unit}`;
                              valueClass += ' pill-pos';
                              rowClassName = 'row-in';
                            } else {
                              const toName = row.toUid ? (uidToPseudo[row.toUid] || 'Utilisateur') : 'Utilisateur';
                              typeLabel = `Envoyé à ${toName}`;
                              typeIcon = <IconTransferOut />;
                              value = `-${row.qty} ${unit}`;
                              valueClass += ' pill-neg';
                              rowClassName = 'row-out';
                            }
                            break;
                          }
                          default: break;
                        }

                        const uniqueKey = `row-${row.id}-${index}`;

                        return (
                          <tr key={uniqueKey} className={rowClassName} style={rowStyle}>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <span className={valueClass}>{value}</span>
                            </td>
                            <td className="td-date">
                              <span className="date-long">{formattedDate}</span>
                              <span className="date-short">{formattedDateShort}</span>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {ratingVal ? (
                                <span className="td-rating">
                                  <FaStar aria-hidden="true" /> {ratingVal}
                                </span>
                              ) : <span style={{ color: 'var(--eb-subtle)' }}>—</span>}
                            </td>
                            <td className="td-type" title={typeLabel} aria-label={typeLabel}>
                              <span className="label">{typeIcon}<span className="label-text">{typeLabel}</span></span>
                              <span className="icon-only">{typeIcon}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {rows.length > 5 && (
                  <button
                    type="button"
                    className="eb-btn eb-btn--secondary show-more"
                    onClick={() => setShowAll((s) => !s)}
                  >
                    {showAll ? 'Afficher moins' : 'Afficher plus'}
                  </button>
                )}
              </>
            );
          })()}
        </section>
      </div>
    </div>
  );
};

const CFV_STYLES = `
.cfv-page {
  min-height: 100vh;
  min-height: 100dvh;
  padding: 16px 16px 56px;
  background:
    radial-gradient(900px 480px at 50% -12%, rgba(39, 76, 116, 0.12), transparent 62%),
    var(--eb-bg);
}
.cfv-main {
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.cfv-topbar { display: flex; align-items: center; }
.cfv-back { min-height: 40px; gap: 8px; }
.cfv-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 40px 20px;
  text-align: center;
  background: var(--eb-surface);
  border: 1px solid var(--eb-border);
  border-radius: var(--eb-radius-lg);
  box-shadow: var(--eb-shadow-sm);
}
.cfv-state--flat { padding: 28px 20px; box-shadow: none; }

.cfv-hero {
  width: 100%;
  max-width: 440px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  animation: eb-fade-up 0.4s var(--eb-ease) both;
}
.cfv-merchant { display: flex; flex-direction: column; align-items: center; gap: 12px; text-align: center; }
.cfv-merchant__name { margin: 0; font-size: 22px; font-weight: 700; letter-spacing: -0.02em; word-break: break-word; }
.cfv-logo {
  width: 84px;
  height: 84px;
  object-fit: cover;
  border-radius: 50%;
  background: var(--eb-surface);
  border: 4px solid var(--eb-surface);
  box-shadow: var(--eb-shadow-md);
}
.cfv-code {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 18px;
  border: 1.5px dashed var(--eb-border-strong);
  border-radius: var(--eb-radius);
  background: var(--eb-surface);
  font-family: var(--eb-font-mono);
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.18em;
  color: var(--eb-text);
}

/* Carte fidélité */
.cfv-loyalty, .cfv-points {
  width: 100%;
  border-radius: var(--eb-radius-xl);
  overflow: hidden;
  background: var(--eb-surface);
  border: 1px solid var(--eb-border);
  box-shadow: var(--eb-shadow-lg);
  text-align: left;
}
.cfv-loyalty__head, .cfv-points__head {
  position: relative;
  overflow: hidden;
  padding: 22px 24px 24px;
  background: linear-gradient(135deg, var(--eb-brand-800) 0%, var(--eb-brand) 100%);
  text-align: center;
}
.cfv-loyalty__deco { position: absolute; border-radius: 50%; background: rgba(255,255,255,0.06); }
.cfv-loyalty__deco--a { top: -36px; right: -36px; width: 130px; height: 130px; }
.cfv-loyalty__deco--b { bottom: -24px; left: -24px; width: 84px; height: 84px; background: rgba(255,255,255,0.04); }
.cfv-loyalty__eyebrow {
  color: rgba(255,255,255,0.68);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 6px;
}
.cfv-loyalty__count {
  color: #fff;
  font-size: 44px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
}
.cfv-loyalty__total { font-size: 20px; font-weight: 600; color: rgba(255,255,255,0.55); margin-left: 6px; letter-spacing: 0; }
.cfv-loyalty__caption { color: rgba(255,255,255,0.72); font-size: 13.5px; font-weight: 500; margin-top: 6px; }
.cfv-progress { margin-top: 18px; height: 8px; border-radius: 999px; background: rgba(255,255,255,0.16); overflow: hidden; }
.cfv-progress__fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg, var(--eb-brand-300), #ffffff);
  transition: width 0.5s var(--eb-ease);
}
.cfv-loyalty__body { padding: 22px 20px 20px; }
.cfv-almost {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  padding: 10px 14px;
  margin-bottom: 16px;
  border-radius: var(--eb-radius);
  background: var(--eb-warning-bg);
  border: 1px solid #fde3b0;
  color: var(--eb-warning);
  font-size: 14px;
  font-weight: 600;
}
.cfv-reward {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: var(--eb-radius-lg);
  background: var(--eb-surface-2);
  border: 1px solid var(--eb-border);
}
.cfv-reward__icon {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: var(--eb-radius);
  display: grid;
  place-items: center;
  background: var(--eb-brand);
  color: #fff;
  box-shadow: 0 6px 16px rgba(39, 76, 116, 0.25);
}
.cfv-eyebrow {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--eb-muted);
  margin-bottom: 2px;
}
.cfv-reward__gift { font-size: 16px; font-weight: 700; color: var(--eb-text); overflow-wrap: anywhere; }

/* Fidélité à points */
.cfv-points__list { padding: 16px; display: flex; flex-direction: column; gap: 10px; }
.cfv-tier {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: var(--eb-radius);
  border: 1px solid var(--eb-border);
  background: var(--eb-surface-2);
}
.cfv-tier.is-reached { background: var(--eb-warning-bg); border-color: #fde3b0; }
.cfv-tier__icon {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: var(--eb-surface);
  border: 1px solid var(--eb-border);
  color: var(--eb-subtle);
}
.cfv-tier.is-reached .cfv-tier__icon { background: var(--eb-warning); border-color: var(--eb-warning); color: #fff; }
.cfv-tier__gift { margin: 0; font-size: 15px; color: var(--eb-text); overflow-wrap: anywhere; }
.cfv-tier__pts { margin: 2px 0 0; font-size: 13px; font-weight: 600; color: var(--eb-muted); }

/* Historique */
.cfv-history {
  background: var(--eb-surface);
  border: 1px solid var(--eb-border);
  border-radius: var(--eb-radius-lg);
  box-shadow: var(--eb-shadow-sm);
  padding: 20px;
  text-align: center;
}
.cfv-history__title { margin: 0 0 14px; font-size: 17px; font-weight: 700; text-align: left; }

/* Modales */
.cfv-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 35, 56, 0.55);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}
.cfv-modal {
  position: relative;
  width: 100%;
  max-width: 420px;
  max-height: calc(100vh - 32px);
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  background: var(--eb-surface);
  border-radius: var(--eb-radius-xl);
  box-shadow: var(--eb-shadow-lg);
  padding: 24px;
}
.cfv-modal--center { text-align: center; }
.cfv-modal__center { display: flex; flex-direction: column; align-items: center; text-align: center; }
.cfv-modal .cfv-logo { width: 72px; height: 72px; }
.cfv-modal__title { margin: 12px 0 4px; font-size: 19px; font-weight: 700; }
.cfv-modal__text { margin: 0 0 20px; color: var(--eb-muted); font-size: 14.5px; }
.cfv-modal__actions { display: flex; justify-content: center; margin-top: 16px; }
.cfv-later { margin-top: 12px; color: var(--eb-muted); }
.cfv-social { display: flex; flex-direction: column; gap: 10px; align-items: stretch; }
.cfv-social__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  min-height: 48px;
  padding: 0 20px;
  border-radius: var(--eb-radius);
  font-weight: 600;
  font-size: 15px;
  text-decoration: none;
  white-space: nowrap;
  transition: filter 0.15s ease, background-color 0.15s ease;
}
.cfv-social__btn--google { background: #fff; color: #3c4043; border: 1px solid var(--eb-border-strong); box-shadow: var(--eb-shadow-xs); }
.cfv-social__btn--google:hover { background: var(--eb-surface-2); color: #3c4043; }
.cfv-social__btn--facebook { background: #1877F2; color: #fff; border: 1px solid #1877F2; }
.cfv-social__btn--facebook:hover { filter: brightness(0.94); color: #fff; }
.cfv-stars { display: flex; justify-content: center; gap: 6px; margin: 4px 0 20px; }
.cfv-star {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: var(--eb-radius-sm);
  background: transparent;
  color: var(--eb-border-strong);
  font-size: 30px;
  cursor: pointer;
  transition: transform 0.12s ease, color 0.12s ease;
}
.cfv-star.is-on { color: #f5a524; }
.cfv-star.is-current { transform: translateY(-2px) scale(1.06); }
.cfv-star:focus-visible { outline: none; box-shadow: var(--eb-ring); }
.cfv-textarea {
  width: 100%;
  padding: 12px 14px;
  font-size: 15px;
  line-height: 1.45;
  resize: vertical;
  min-height: 96px;
  background: var(--eb-surface);
}
.cfv-counter { margin-top: 6px; text-align: right; color: var(--eb-muted); font-size: 13px; }

.confetti-banner {
  position: absolute;
  top: -14px;
  left: 0;
  right: 0;
  height: 120px;
  overflow: visible;
  pointer-events: none;
  text-align: center;
}
.confetti-message {
  margin: 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  background: var(--eb-success);
  color: #fff;
  border-radius: 999px;
  font-weight: 700;
  font-size: 14px;
  box-shadow: 0 10px 22px rgba(18, 128, 92, 0.28);
  pointer-events: none;
}
.confetti-piece {
  position: absolute;
  width: 9px;
  height: 16px;
  border-radius: 4px;
  opacity: 0;
  animation-name: confetti-fall;
  animation-timing-function: ease-out;
  animation-fill-mode: forwards;
  pointer-events: none;
}
@keyframes confetti-fall {
  0% { transform: translate3d(0, -70px, 0) rotate(var(--start-rot, 0deg)); opacity: 1; }
  75% { opacity: 1; }
  100% { transform: translate3d(var(--drift, 0px), 120px, 0) rotate(var(--end-rot, 320deg)); opacity: 0; }
}

/* Tableau historique */
.table-fidelity-wrap {
  width: 100%;
  overflow-x: auto;
  border: 1px solid var(--eb-border);
  border-radius: var(--eb-radius);
}
.table-fidelity {
  width: 100%;
  border-collapse: collapse;
  background: var(--eb-surface);
  font-size: 14px;
}
.table-fidelity thead th {
  background: var(--eb-surface-2);
  color: var(--eb-muted);
  font-weight: 600;
  font-size: 12.5px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid var(--eb-border);
  white-space: nowrap;
}
.table-fidelity th,
.table-fidelity td {
  padding: 12px 14px;
  text-align: left;
  vertical-align: middle;
}
.table-fidelity tbody tr { border-bottom: 1px solid var(--eb-border); transition: background-color .15s ease; }
.table-fidelity tbody tr:last-child { border-bottom: 0; }
.table-fidelity tbody tr:hover { background: var(--eb-surface-2); }
.table-fidelity .td-date { color: var(--eb-muted); font-size: 13.5px; white-space: nowrap; font-variant-numeric: tabular-nums; }
.td-rating { display: inline-flex; align-items: center; gap: 4px; font-weight: 600; color: var(--eb-text-2); }
.td-rating svg { color: #f5a524; }
/* Format long par défaut (> 360px), format court en dessous */
.date-short { display: none; }
.date-long  { display: inline; }
@media (max-width: 360px) {
  .date-short { display: inline; }
  .date-long  { display: none; }
}
.table-fidelity .td-type { font-weight: 500; color: var(--eb-text-2); }
.td-type .label { display: inline-flex; align-items: center; gap: 8px; }
.td-type .label-text { overflow-wrap: anywhere; }
.td-type .icon-only { display: none; }
.type-ico {
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  border-radius: 50%;
  display: inline-grid;
  place-items: center;
  font-size: 12px;
}
.type-ico--scan { background: var(--eb-brand-50); color: var(--eb-brand); }
.type-ico--gift { background: var(--eb-warning-bg); color: var(--eb-warning); }
.type-ico--in   { background: var(--eb-success-bg); color: var(--eb-success); }
.type-ico--out  { background: var(--eb-danger-bg); color: var(--eb-danger); }

.pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid transparent;
  font-size: 13px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.pill-scan { background: var(--eb-brand-50); color: var(--eb-brand); border-color: var(--eb-brand-100); }
.pill-gift { background: var(--eb-warning-bg); color: var(--eb-warning); border-color: #fde3b0; }
.pill-pos  { background: var(--eb-success-bg); color: var(--eb-success); border-color: #c6f0da; }
.pill-neg  { background: var(--eb-danger-bg); color: var(--eb-danger); border-color: #fbd0cb; }

.row-gift   { background: #fffcf5; }
.row-in     { background: #f7fdf9; }
.row-out    { background: #fffafa; }

.show-more { margin-top: 14px; }

/* --- < 500px: Type = icône seule --- */
@media (max-width: 500px) {
  .td-type .label { display: none; }
  .td-type .icon-only { display: inline-flex; align-items: center; justify-content: center; width: 100%; }
}
/* --- < 480px --- */
@media (max-width: 480px) {
  .cfv-page { padding: 12px 12px 48px; }
  .cfv-history { padding: 16px 12px; }
  .cfv-loyalty__count { font-size: 40px; }
  .table-fidelity th, .table-fidelity td { padding: 10px 8px; }
  .pill { padding: 3px 8px; font-size: 12.5px; }
  .cfv-overlay { align-items: flex-end; padding: 0; }
  .cfv-modal {
    max-width: none;
    max-height: calc(100dvh - 24px);
    border-radius: var(--eb-radius-xl) var(--eb-radius-xl) 0 0;
    padding: 24px 20px calc(20px + env(safe-area-inset-bottom, 0px));
  }
  .cfv-modal__actions .eb-btn { width: 100%; }
}
`;

export default ClientFidViewer;
